// supabase/functions/_shared/court-sheet.ts
// The club's court sheet, pasted as tab-separated text. Columns:
// Week, Date, Day, Start, End, Duration, Status, Court, Status, Notes, Payment.
// First Status BOOKED or blank -> held; "-" / NO BOOKED -> skipped. Times are Asia/Makassar wall clock.
// A blank Date means "same day as the row above" (merged cells in the sheet). Pure; unit-tested.
// If the first non-blank line has a "Date" cell it is a header: columns are then found by name (Date, Start, End, the FIRST Status,
// Court, Notes, and optionally "Booked by JPS" - the club's tick that the court is under the players' names), so extra
// columns (Players, Jungle emails, ...) and a different order are fine. No header -> the fixed A-K layout above.

/** Jungle Padel Sanur has no Court 4: its center court sits there, so the center court is stored as "4". */
export const CENTER_COURT = "4";
/** How the club writes the center court: "CC", "Center", "Centre court". */
export const CENTER_COURT_RE = /^(cc|cent(er|re)( court)?)$/i;

/** "2" -> "Court 2", "4" -> "Center court"; "Court 3" stays. */
export const courtName = (label: string): string => {
  const t = label.trim();
  return t === CENTER_COURT ? "Center court" : /^court\b/i.test(t) ? t : `Court ${t}`;
};

export type SlotRow = {
  startsAt: string; endsAt: string; courtLabel: string; status: "held" | "skipped"; note: string;
  /** Present only when the sheet has a "Booked by JPS" column. */
  namesEntered?: boolean;
  /** 0-based index of the input line this row came from (blank lines count), so a caller can find the sheet row. */
  line?: number;
};

const MONTHS: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};
const HELD = new Set(["", "BOOKED"]);
const NOT_BOOKED = new Set(["-", "NO BOOKED", "NOT BOOKED"]);
const pad = (n: number) => String(n).padStart(2, "0");

export function parseSheetDate(raw: string): string | null {
  const m = raw.trim().match(/^(\d{1,2})\s+([A-Za-z]{3})[A-Za-z]*\.?\s+(\d{4})$/);
  if (!m) return null;
  const month = MONTHS[m[2].toLowerCase()];
  if (!month) return null;
  const ymd = `${m[3]}-${pad(month)}-${pad(Number(m[1]))}`;
  const d = new Date(`${ymd}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === ymd ? ymd : null;
}

export function parseSheetTime(raw: string): string | null {
  const m = raw.trim().match(/^(\d{1,2})[:.](\d{2})(?::\d{2})?\s*([AaPp][Mm])?$/);
  if (!m) return null;
  let h = Number(m[1]);
  const min = Number(m[2]);
  if (m[3]) {
    if (h < 1 || h > 12) return null;
    h = (h % 12) + (m[3].toLowerCase() === "pm" ? 12 : 0);
  }
  if (h > 23 || min > 59) return null;
  return `${pad(h)}:${pad(min)}`;
}

type Cols = { date: number; start: number; end: number; status: number; court: number; notes: number; booked: number };
const FIXED_COLS: Cols = { date: 1, start: 3, end: 4, status: 6, court: 7, notes: 9, booked: -1 };
const TICKED = new Set(["TRUE", "YES", "✓", "✔", "X", "1"]);

function headerCols(cells: string[]): Cols | null {
  const names = cells.map((c) => c.trim().toLowerCase());
  if (!names.includes("date")) return null;
  const at = (n: string, fallback: number) => { const i = names.indexOf(n); return i >= 0 ? i : fallback; };
  return {
    date: at("date", FIXED_COLS.date), start: at("start", FIXED_COLS.start), end: at("end", FIXED_COLS.end),
    status: at("status", FIXED_COLS.status), court: at("court", FIXED_COLS.court), notes: at("notes", -1), booked: at("booked by jps", -1),
  };
}

export function parseCourtSheet(text: string): { rows: SlotRow[]; skipped: number; duplicates: number } {
  const rows: SlotRow[] = [];
  const seen = new Set<string>();
  let skipped = 0;
  let duplicates = 0;
  let lastDate: string | null = null;
  let ix = FIXED_COLS;
  let first = true;

  for (const [lineNo, line] of text.split(/\r?\n/).entries()) {
    if (!line.trim()) continue;
    const cols = line.split("\t").map((c) => c.trim());
    const header = first ? headerCols(cols) : null;
    first = false;
    if (header) { ix = header; continue; }                              // header row: read the column positions from it
    if ((cols[ix.date] ?? "").toLowerCase() === "date") continue;      // a repeated header row
    const rawDate = cols[ix.date] ?? "";
    const date: string | null = rawDate ? parseSheetDate(rawDate) : lastDate;
    if (rawDate) lastDate = date; // an unreadable date must not leak into the blank-date rows below it
    const start = parseSheetTime(cols[ix.start] ?? "");
    const end = parseSheetTime(cols[ix.end] ?? "");
    const rawStatus = cols[ix.status] ?? "";
    const status = rawStatus.toUpperCase().replace(/\s+/g, " ");
    const rawCourt = (cols[ix.court] ?? "").replace(/^court\s*/i, "").trim();
    const courtLabel = CENTER_COURT_RE.test(rawCourt) ? CENTER_COURT : rawCourt; // "CC" = the center court, stored as "4"
    const note = ix.notes >= 0 ? (cols[ix.notes] ?? "") : "";
    const startsAt = date && start ? `${date}T${start}:00+08:00` : "";
    const endsAt = date && end ? `${date}T${end}:00+08:00` : "";
    const skip = (why: string) => { rows.push({ startsAt, endsAt, courtLabel, status: "skipped", note: why }); skipped++; };

    if (!date) { skip(`unreadable date "${rawDate}"`); continue; }
    if (!start || !end) { skip(`unreadable time "${cols[ix.start] ?? ""}"-"${cols[ix.end] ?? ""}"`); continue; }
    if (NOT_BOOKED.has(status)) { skip(note ? `not booked: ${note}` : "not booked"); continue; }   // the club's note rides along (tournament days)
    if (!HELD.has(status)) { skip(`unknown status "${rawStatus}"`); continue; }
    if (!courtLabel) { skip("no court"); continue; }
    if (end <= start) { skip("ends before it starts"); continue; }
    const key = `${startsAt}|${courtLabel}`;
    if (seen.has(key)) { duplicates++; continue; }
    seen.add(key);
    rows.push(ix.booked >= 0
      ? { startsAt, endsAt, courtLabel, status: "held", note, namesEntered: TICKED.has((cols[ix.booked] ?? "").toUpperCase()), line: lineNo }
      : { startsAt, endsAt, courtLabel, status: "held", note, line: lineNo });
  }
  return { rows, skipped, duplicates };
}
